import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { claimApi, termsApi } from '../services/api';
import { readClaimToken, clearClaimToken, shouldRetainToken } from '../services/claimToken';

// Messages per server-declared error code. Anything not listed falls back to a generic
// line -- the client must never depend on recognising every code.
const CODE_KEYS = {
  TOKEN_UNKNOWN: 'claim.codeTokenUnknown',
  TOKEN_EXPIRED: 'claim.codeTokenExpired',
  ALREADY_CLAIMED: 'claim.codeAlreadyClaimed',
  SUPERSEDED: 'claim.codeSuperseded',
  APPLICATION_REJECTED: 'claim.codeRejected',
  CALLER_HAS_PROVIDER: 'claim.codeCallerHasProvider',
  ADDRESS_HAS_PROVIDER: 'claim.codeAddressHasProvider',
  NOT_FOUND_OR_FORBIDDEN: 'claim.codeNotFound',
};

const ClaimHandler = () => {
  const {
    currentUser,
    refreshProviderData,
    checkProviderStatus,
    sendVerificationEmail,
    reloadUser,
  } = useAuth();
  const [busy, setBusy] = useState(false);
  const { t, i18n } = useTranslation();
  const [error, setError] = useState(null);
  const [claimable, setClaimable] = useState([]);
  // undefined = still loading, null = dormant (no active release), object = must be accepted
  const [terms, setTerms] = useState(undefined);
  const [termsError, setTermsError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // All three calls are required. AuthContext classified this user as
  // providerStatus 'not_submitted' at authentication, because no provider existed yet.
  // Refreshing the Firebase token updates custom claims but touches neither
  // providerData nor providerStatus -- so without the last two the applicant is told
  // the claim worked and then sits on the "apply to become a provider" screen.
  const afterClaim = useCallback(async () => {
    if (currentUser) await currentUser.getIdToken(true);
    await refreshProviderData();
    await checkProviderStatus(currentUser);
  }, [currentUser, refreshProviderData, checkProviderStatus]);

  const handleFailure = useCallback((err) => {
    const body = err?.body || null;
    if (!shouldRetainToken(body)) clearClaimToken();
    setError(t((body && CODE_KEYS[body.code]) || 'claim.genericFailure'));
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    termsApi.getActive(i18n.language)
      .then((active) => {
        if (cancelled) return;
        // apiRequest RESOLVES null on a 401 (it clears the token and redirects). Optional
        // chaining would turn that into `required: undefined` -> falsy -> "dormant", and the
        // auto-claim below would then take ownership of the business with no acceptance. Only
        // an explicit object counts as an answer.
        if (!active || typeof active.required !== 'boolean') {
          setTerms(null);
          setTermsError(true);
          return;
        }
        setTerms(active.required ? active : null);
      })
      .catch(() => {
        // NOT dormancy. Block the claim and offer a retry rather than silently claiming
        // without recording an acceptance.
        if (!cancelled) { setTerms(null); setTermsError(true); }
      });
    return () => { cancelled = true; };
  }, [i18n.language]);

  // Nothing is claimed until this is true. The token claim used to fire automatically on
  // mount, which would take ownership of the business before the owner had seen the terms.
  const readyToClaim = !termsError && (terms === null || Boolean(terms && agreed));

  // Auto-claim ONLY while dormant. With no active release there is nothing for the owner to
  // agree to, so the original email-link UX is preserved exactly. Once terms are active the
  // claim waits for an explicit, informed click -- claiming the business before the owner has
  // seen the contract is the behaviour this gate exists to prevent.
  const autoClaimed = useRef(false);
  useEffect(() => {
    if (terms !== null || termsError) return;
    if (!readClaimToken() || autoClaimed.current) return;
    autoClaimed.current = true;
    runTokenClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, termsError]);

  const runTokenClaim = useCallback(async () => {
    const token = readClaimToken();
    if (!token || !readyToClaim) return;
    setBusy(true);
    setError(null);
    try {
      await claimApi.claimWithToken(token, terms?.version ?? null, terms?.locale ?? null);
      clearClaimToken();
      await afterClaim();
    } catch (err) {
      handleFailure(err);
    } finally {
      setBusy(false);
    }
  }, [readyToClaim, terms, afterClaim, handleFailure]);

  // Verified-email fallback: covers a link opened on another device, ignored, or
  // expired. Only runs when there is no token to try.
  useEffect(() => {
    if (readClaimToken()) return;
    if (!currentUser) return;
    if (!currentUser.emailVerified) { setNeedsVerification(true); return; }
    claimApi.listClaimable().then(setClaimable).catch(() => setClaimable([]));
  }, [currentUser]);

  const claimById = async (applicationId) => {
    // Should be unreachable: the buttons below are disabled until readyToClaim. Kept so a future
    // caller cannot claim without acceptance, but it must never be the ONLY feedback -- silently
    // doing nothing on a click is exactly what this guard used to cause.
    if (!readyToClaim) return;
    setBusy(true);
    setError(null);
    try {
      await claimApi.claimApplication(
        applicationId, terms?.version ?? null, terms?.locale ?? null,
      );
      await afterClaim();
    } catch (err) {
      handleFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    const ok = await sendVerificationEmail();
    setVerificationSent(ok);
  };

  const recheckVerification = async () => {
    const verified = await reloadUser();
    if (verified) setNeedsVerification(false);
  };

  if (busy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {termsError && (
        <Alert
          severity="error"
          action={<Button size="small" onClick={() => window.location.reload()}>{t('terms.retry')}</Button>}
        >
          {t('terms.loadFailed')}
        </Alert>
      )}

      {terms && (
        <Alert severity="info">
          <Typography sx={{ mb: 1 }}>{t('terms.mustAccept', { version: terms.version })}</Typography>
          {/* sandbox WITHOUT allow-same-origin: uploaded documents are untrusted content. */}
          <Box
            component="iframe"
            title={terms.title || 'Provider Terms'}
            src={terms.url}
            sandbox=""
            sx={{ width: '100%', height: 320, border: '1px solid', borderColor: 'divider', mb: 1 }}
          />
          <FormControlLabel
            control={(
              <Checkbox
                data-testid="terms-agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
            )}
            label={t('terms.agreeCheckbox')}
          />
        </Alert>
      )}

      {terms && !termsError && readClaimToken() && (
        <Button
          data-testid="claim-submit"
          variant="contained"
          disabled={!readyToClaim}
          onClick={runTokenClaim}
        >
          {t('claim.claimMyBusiness')}
        </Button>
      )}

      {needsVerification && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={resendVerification}>
                {verificationSent ? t('claim.verificationSent') : t('claim.resend')}
              </Button>
              <Button size="small" onClick={recheckVerification}>{t('claim.iveVerified')}</Button>
            </Stack>
          }
        >
          {t('claim.verifyEmailPrompt')}
        </Alert>
      )}

      {claimable.length > 0 && (
        <Alert severity="success">
          <Typography sx={{ mb: 1 }}>
            {claimable.length === 1
              ? t('claim.foundOne', { name: claimable[0].businessName })
              : t('claim.foundMany')}
          </Typography>
          {/* Say WHY the buttons are disabled. Without this the owner clicks a live-looking
              button and nothing happens, with no error and no explanation. */}
          {!readyToClaim && !termsError && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('terms.mustAcceptBeforeClaim')}
            </Typography>
          )}
          <Stack spacing={1}>
            {claimable.map((app) => (
              <Button
                key={app.applicationId}
                variant="contained"
                size="small"
                disabled={!readyToClaim}
                onClick={() => claimById(app.applicationId)}
              >
                {t('claim.claimButton', { name: app.businessName, city: app.city })}
              </Button>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export default ClaimHandler;
